import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const primaryLinks = [
  { to: "/", label: "首页" },
  { to: "/category/entertainment", label: "娱乐圈" },
  { to: "/category/influencer", label: "网红圈" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const dropRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(event.target as Node)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/?q=${encodeURIComponent(value)}` : "/");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
      isActive
        ? "text-primary-600 font-medium bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-fire-line text-2xl" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl md:text-2xl text-primary-600">
              瓜友会网
            </span>
            <span className="hidden sm:block text-[11px] text-foreground-400 mt-1 tracking-wide">
              吃瓜现场 · 实时热瓜
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {primaryLinks.slice(0, 1).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}

          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => setDropOpen((prev) => !prev)}
              className="px-3 py-2 text-sm rounded-md text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              aria-expanded={dropOpen}
            >
              热门瓜田
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  dropOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>
            {dropOpen && (
              <div className="absolute left-0 top-full mt-2 w-[420px] p-3 rounded-lg bg-background-50 border border-background-200 shadow-sm">
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => setDropOpen(false)}
                      className="flex items-start gap-2 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900">
                          {cat.name}
                        </span>
                        <span className="block text-xs text-foreground-400 truncate">
                          {cat.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {primaryLinks.slice(1).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            className="hidden lg:flex items-center gap-2 h-10 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors"
            role="search"
          >
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜瓜、搜话题"
              aria-label="搜索瓜田内容"
              className="bg-transparent outline-none text-sm w-36 xl:w-44 text-foreground-800 placeholder:text-foreground-400"
            />
          </form>

          <Link
            to="/about#submit"
            className="hidden md:inline-flex items-center gap-1 h-10 px-4 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-megaphone-line" aria-hidden="true"></i>
            我要爆料
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开导航菜单"
            className="md:hidden w-10 h-10 rounded-md border border-background-200 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i
              className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 h-11 px-3 rounded-full bg-background-100 border border-background-200 mb-3"
            role="search"
          >
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜瓜、搜话题"
              aria-label="搜索瓜田内容"
              className="bg-transparent outline-none text-sm flex-1 text-foreground-800 placeholder:text-foreground-400"
            />
          </form>
          <div className="flex flex-col">
            {primaryLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-sm text-foreground-800 border-b border-background-100 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="mt-3">
            <p className="text-xs text-foreground-400 mb-2">热门瓜田</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 cursor-pointer"
                >
                  <i className={cat.icon} aria-hidden="true"></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/about#submit"
            onClick={() => setMenuOpen(false)}
            className="mt-4 inline-flex w-full items-center justify-center gap-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm cursor-pointer"
          >
            <i className="ri-megaphone-line" aria-hidden="true"></i>
            我要爆料
          </Link>
        </div>
      )}
    </header>
  );
}